<template>
  <f7-popup
    ref="chatPopup"
    class="agroia-chat-popup"
    :opened="showChatPopup"
    :close-by-backdrop-click="true"
    data-tour-id="agroia-chat-popup"
    @popup:open="onOpen"
    @popup:closed="onClosed"
  >
    <f7-page class="chat-main-div">
      <div class="title">
        <div class="innerTitle">
          <div class="title-left">
            <div class="interpretation-ai-icon"></div>
            <div>{{ title || $t('agroIA.chatPopupTitle') }}</div>
          </div>
          <div>
            <f7-link @click="closePopup">
              <i class="f7-icons">multiply</i>
            </f7-link>
          </div>
        </div>
      </div>

      <div class="chat-body">
        <!-- Historial de la muestra activa: mensajes reales y placeholders de carga -->
        <div
          ref="messages"
          class="chat-messages"
          data-tour-id="agroia-chat-messages"
        >
          <div
            v-for="m in messages"
            :key="m.id"
            :class="['chat-msg', m.role]"
          >
            <div
              v-if="m.role === 'assistant'"
              class="chat-author-bot-icon"
            ></div>
            <div class="chat-msg-content">
              <div class="chat-msg-author">
                <template v-if="m.role === 'assistant'">
                  <span class="chat-author-name chat-author-name-bot">
                    {{ $t('agroIA.chatAssistantName') }}
                  </span>
                </template>
                <template v-else>
                  <span class="chat-author-name">
                    {{ $t('agroIA.chatYouLabel') }}:
                  </span>
                </template>
              </div>
              <div class="bubble">
                <f7-preloader
                  v-if="m.pending"
                  size="16"
                ></f7-preloader>
                <div v-else v-html="formatHTMLText(m.text)"></div>
              </div>
              <div v-if="!m.pending" class="chat-msg-time">
                {{ getMessageTime(m) }}
              </div>
            </div>
          </div>
        </div>

        <div class="question-card" data-tour-id="agroia-chat-input">
          <!-- El borrador vive en el componente; solo se persiste al enviar -->
          <div class="question-input">
            <input
              ref="questionInput"
              class="question-native-input"
              type="text"
              :placeholder="$t('agroIA.chatQuestionPlaceholder')"
              :value="draft"
              :disabled="isLoading || !codMuestra"
              @input="onDraftInput"
              @keydown.enter.prevent="send"
            />
          </div>

          <button
            class="send-icon-btn"
            type="button"
            :disabled="isLoading || !codMuestra"
            :aria-label="$t('agroIA.send')"
            @click="send"
          >
            <i class="f7-icons">paperplane</i>
          </button>
        </div>
      </div>
    </f7-page>
  </f7-popup>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import marked from 'marked';
import DOMPurify from 'dompurify';
import { IS_LOCAL_TEST, NOTIFY_TIME_LONG } from '@/js/constants';

export default {
  name: 'AgroIAChatPanel',

  props: {
    title: { type: String, default: '' },
    codMuestra: { type: String, default: '' },
    interpretation: { type: String, default: '' },
  },

  data() {
    return {
      draft: '',
      bootstrapped: false,
    };
  },

  computed: {
    ...mapState('agroIA', [
      'showChatPopup',
      'isLoading',
      'chatsBySample',
    ]),

    messages() {
      // Normaliza a array para simplificar el template y evitar null-checks adicionales
      const key = this.codMuestra || '__no_sample__';
      return (
        (this.chatsBySample[key] &&
          this.chatsBySample[key].messages) ||
        []
      );
    },
  },

  watch: {
    // Si cambia la muestra mientras el popup está abierto, prepara el chat de esa muestra
    codMuestra() {
      this.bootstrapped = false;
      if (this.showChatPopup) {
        this.$nextTick(() => {
          this.onOpen();
        });
      }
    },
  },

  methods: {
    ...mapActions('agroIA', [
      'setShowChatPopup',
      'ensureChatForSample',
      'bootstrapChatFromInterpretation',
      'sendChatQuestion',
    ]),

    formatHTMLText(texto) {
      const normalized = (texto || '').replace(/\\n/g, '\n');
      const rawHtml = marked.parse(normalized, {
        breaks: true,
        gfm: true,
      });
      return DOMPurify.sanitize(rawHtml);
    },

    closePopup() {
      this.$f7ready(() => {
        const popup = this.$refs.chatPopup;

        if (popup && popup.f7Popup) {
          popup.f7Popup.close();
          return;
        }

        if (this.$f7 && this.$f7.popup) {
          this.$f7.popup.close('.agroia-chat-popup');
        }
      });
    },

    onClosed() {
      this.setShowChatPopup(false);
      this.draft = '';
      this.bootstrapped = false;
    },

    onOpen() {
      if (!this.codMuestra) {
        return;
      }

      // Asegura el contenedor de chat por muestra antes de leer/escribir mensajes
      this.ensureChatForSample({ codMuestra: this.codMuestra });

      // Inyecta interpretacion base una sola vez por apertura del popup
      if (!this.bootstrapped) {
        this.bootstrapChatFromInterpretation({
          codMuestra: this.codMuestra,
          interpretation: this.interpretation,
          emptyMessage: this.$t('agroIA.noInterpretationGenerated'),
        });

        this.bootstrapped = true;
        this.$nextTick(this.scrollToBottom);
      }

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.focusQuestionInput();
        });
      });
    },

    onDraftInput(e) {
      this.draft = (e && e.target && e.target.value) || '';
    },
    focusQuestionInput() {
      const input = this.$refs.questionInput;
      if (input && typeof input.focus === 'function') {
        input.focus();
      }
    },
    getAgroIAAnalyticsContext() {
      const authenticatedUser =
        this.$store?.state?.user?.authenticatedUser || {};
      const contact = this.$store?.state?.contact || {};
      const contactInfo =
        this.$store?.getters?.['contact/contactInfo'] || {};
      const language =
        authenticatedUser?.language_code ||
        authenticatedUser?.contacts?.[0]?.language;
      const companyCode =
        contact?.company ||
        authenticatedUser?.contacts?.[0]?.companies?.[0]?.code;
      // Pais para agregacion en GA:
      // 1) si el contacto activo expone pais, usamos ese valor.
      // 2) si no, usamos el prefijo de la empresa (ES, CL, US).
      const countryCode =
        contactInfo?.country ||
        (typeof companyCode === 'string' &&
        companyCode.trim().length >= 2
          ? companyCode.trim().substring(0, 2)
          : undefined);

      return {
        sample_code: this.codMuestra || undefined,
        company_code: companyCode || undefined,
        country_code: countryCode || undefined,
        user_language: language || undefined,
      };
    },
    trackAgroIAEvent(eventName, extraParams = {}) {
      if (IS_LOCAL_TEST) return;

      window.gtag?.('event', eventName, {
        ...this.getAgroIAAnalyticsContext(),
        ...extraParams,
      });
    },
    resolveMessageTimestamp(message) {
      if (message && message.createdAt) {
        return Number(message.createdAt);
      }

      // Compatibilidad con mensajes legacy: el id empieza por Date.now().
      const rawId = (message && message.id) || '';
      const tsFromId = Number(String(rawId).split('_')[0]);
      return Number.isFinite(tsFromId) ? tsFromId : null;
    },
    getMessageTime(message) {
      const ts = this.resolveMessageTimestamp(message);
      if (!ts) return '';

      return new Intl.DateTimeFormat(this.$i18n.locale, {
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(ts));
    },

    scrollToBottom() {
      const el = this.$refs.messages;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    },

    scrollToLastAssistantNearest() {
      const container = this.$refs.messages;
      if (!container) return;

      const nodes = container.querySelectorAll('.chat-msg.assistant');
      const last =
        nodes && nodes.length ? nodes[nodes.length - 1] : null;
      if (!last) return;

      // "nearest" => scroll mínimo (NO te lleva al bottom)
      last.scrollIntoView({ block: 'nearest' });
    },

    async send() {
      const q = (this.draft || '').trim();
      if (!q || !this.codMuestra || this.isLoading) return;

      this.trackAgroIAEvent('agroia_chat_question_submit', {
        question_length: q.length,
      });
      this.draft = '';

      try {
        // Inserta mensaje de usuario + placeholder de asistente de forma inmediata
        const p = this.sendChatQuestion({
          codMuestra: this.codMuestra,
          question: q,
        });

        // Espera a que Vue pinte el pending antes del primer ajuste de scroll
        await this.$nextTick();
        await this.$nextTick();
        this.scrollToBottom();

        // Espera respuesta real del backend
        await p;

        // Reajusta el scroll al sustituir el pending por el texto final
        await this.$nextTick();
        await this.$nextTick();
        // En vez de irte al bottom, asegura visibilidad "mínima"
        // (normalmente te deja donde estabas, viendo el inicio)
        this.scrollToLastAssistantNearest();
      } catch (error) {
        if (this.$notifyDX) {
          this.$notifyDX(
            {
              message: this.$t(
                this.$helpers.getFilteredErrorMessage(error.message)
              ),
              width: 550,
            },
            'error',
            NOTIFY_TIME_LONG
          );
        }
      }
    },
  },
};
</script>

<style lang="scss" src="./AgroIAChatPanel.scss"></style>
